import { createAIResponseId } from '../responseId';
import { NativeSurveyResumeResult, recoverNativeSurvey } from './recoveryService';
import { buildSurveyRequest, canUseGovernedSurveyHelp } from './request';
import { SurveyRecovery } from './recovery';
import { hasAnchoredRecoveredCurrent } from './recoveryChain';
import { emptySurveyState, NativeSurveyAction, nativeSurveyReducer, sameSurveyBinding } from './reducer';
import { SurveyBinding, SurveyEvent, SurveyOperation, SurveyRequest } from './types';
import { parseSurveyEvent, utf8Size } from './validation';

let state = emptySurveyState();
const subscribers = new Set<() => void>();
let generation = 0;
let pendingTimer: ReturnType<typeof setTimeout> | undefined;
export const INITIAL_REQUEST_TTL_MS = 30_000;
const MAX_PENDING_INITIAL_REQUESTS = 8;
const MAX_PENDING_INITIAL_EVENTS = 8;
const MAX_PENDING_INITIAL_BYTES = 256 * 1024;
export interface NativeSurveyRequestContext {
  accountId: string;
  agentId: 'maya';
  roomId: string;
}
interface PendingInitialRequest extends NativeSurveyRequestContext {
  responseId: string;
  generation: number;
  expiresAt: number;
  events: SurveyEvent[];
  bytes: number;
  timer: ReturnType<typeof setTimeout>;
}
let requestContext: NativeSurveyRequestContext | undefined;
const pendingInitialRequests = new Map<string, PendingInitialRequest>();
const sameRequestContext = (left?: NativeSurveyRequestContext, right?: NativeSurveyRequestContext) =>
  left?.accountId === right?.accountId && left?.agentId === right?.agentId && left?.roomId === right?.roomId;
const warnNativeSurveyAdmission = (
  reason: string,
  details: Record<string, unknown> = {}
) => console.warn('[NativeSurvey] Interactive event not admitted.', {
  reason,
  ...details,
});
const removePendingInitialRequest = (responseId: string) => {
  const pending = pendingInitialRequests.get(responseId);
  if (pending) clearTimeout(pending.timer);
  pendingInitialRequests.delete(responseId);
};
const clearPendingInitialRequests = () => {
  for (const pending of pendingInitialRequests.values()) clearTimeout(pending.timer);
  pendingInitialRequests.clear();
};
export const setNativeSurveyRequestContext = (context?: NativeSurveyRequestContext) => {
  if (sameRequestContext(requestContext, context)) return;
  requestContext = context;
  generation++;
  clearPendingInitialRequests();
};
export const registerPendingNativeSurveyRequest = (
  context: NativeSurveyRequestContext,
  responseId: string
) => {
  if (!context.accountId || context.agentId !== 'maya' || !context.roomId || !responseId) return false;
  setNativeSurveyRequestContext(context);
  if (pendingInitialRequests.size >= MAX_PENDING_INITIAL_REQUESTS) {
    removePendingInitialRequest(pendingInitialRequests.keys().next().value as string);
  }
  removePendingInitialRequest(responseId);
  const pending: PendingInitialRequest = {
    ...context,
    responseId,
    generation,
    expiresAt: Date.now() + INITIAL_REQUEST_TTL_MS,
    events: [],
    bytes: 0,
    timer: setTimeout(() => removePendingInitialRequest(responseId), INITIAL_REQUEST_TTL_MS),
  };
  pendingInitialRequests.set(responseId, pending);
  return true;
};
export const failPendingNativeSurveyRequest = (responseId: string) => {
  removePendingInitialRequest(responseId);
};
export const resolvePendingNativeSurveyRequest = (
  context: NativeSurveyRequestContext,
  responseId: string,
  sessionId: string
) => {
  const pending = pendingInitialRequests.get(responseId);
  if (!pending || !sessionId || pending.expiresAt < Date.now() || pending.generation !== generation ||
      !sameRequestContext(pending, context) || !sameRequestContext(requestContext, context)) {
    removePendingInitialRequest(responseId);
    return false;
  }
  const events = pending.events;
  removePendingInitialRequest(responseId);
  bindNativeSurvey({ accountId: context.accountId, sessionId, roomId: context.roomId });
  expectNativeSurvey(sessionId, context.roomId, responseId);
  for (const event of events) {
    if (event.chatSessionId === sessionId) receiveNativeSurvey(event);
  }
  console.warn('[NativeSurvey] Initial interactive request correlated.', {
    responseId,
    chatSessionId: sessionId,
    bufferedEvents: events.length,
  });
  return true;
};
const publish = (action: NativeSurveyAction) => {
  const next = nativeSurveyReducer(state, action);
  if (next === state) return;
  state = next;
  subscribers.forEach((subscriber) => subscriber());
};
export const getNativeSurveyState = () => state;
export const getNativeSurveyGeneration = () => generation;
export const subscribeNativeSurvey = (subscriber: () => void) => {
  subscribers.add(subscriber);
  return () => { subscribers.delete(subscriber); };
};
export const bindNativeSurvey = (binding?: SurveyBinding) => {
  if (!sameSurveyBinding(state.binding, binding)) {
    generation++;
    clearPendingInitialRequests();
    clearTimeout(pendingTimer);
    publish({ type: 'bind', binding });
  }
};
export const hydrateNativeSurveyResume = (
  accountId: string,
  result: NativeSurveyResumeResult,
  expectedGeneration: number
) => {
  if (generation !== expectedGeneration || !result.chatSessionId || !result.recovery ||
      (result.status !== 'resume' && result.status !== 'terminal') || !result.sessionStatus) return false;
  const binding = { accountId, sessionId: result.chatSessionId, roomId: result.roomId };
  generation++;
  clearPendingInitialRequests();
  clearTimeout(pendingTimer);
  publish({ type: 'hydrate', binding, mode: result.status, welcomeText: result.welcomeText,
    sessionStatus: result.sessionStatus, recovery: result.recovery });
  return sameSurveyBinding(state.binding, binding) && state.recoveryStatus === 'ready';
};
export const refreshNativeSurveyRecovery = (
  binding: SurveyBinding,
  recovery: SurveyRecovery,
  expectedGeneration: number
) => {
  if (generation !== expectedGeneration || !sameSurveyBinding(state.binding, binding) ||
      recovery.chatSessionId !== binding.sessionId) return false;
  const current = recovery.items[recovery.items.length - 1]?.presentation;
  if (!current) return false;
  const terminal = current.status === 'completed' || current.status === 'ended';
  generation++;
  clearPendingInitialRequests();
  clearTimeout(pendingTimer);
  publish({
    type: 'hydrate',
    binding,
    mode: terminal ? 'terminal' : 'resume',
    sessionStatus: current.status === 'pending' ? 'inProgress' : current.status,
    recovery,
  });
  return sameSurveyBinding(state.binding, binding) && state.recoveryStatus === 'ready';
};
export const clearNativeSurvey = () => {
  bindNativeSurvey(undefined);
  requestContext = undefined;
  clearPendingInitialRequests();
};
export const invalidateNativeSurvey = () => {
  clearTimeout(pendingTimer);
  publish({ type: 'invalidate' });
};
export const expectNativeSurvey = (sessionId: string | undefined, roomId: string | undefined, responseId: string) => {
  if (sessionId === state.binding?.sessionId && roomId === state.binding?.roomId) publish({ type: 'expect', responseId });
};
export const nativeSurveyCapability = (sessionId?: string, roomId?: string) =>
  !!state.binding && state.binding.sessionId === sessionId && state.binding.roomId === roomId && !state.disconnected && state.phase !== 'unavailable';
export const consumeNativeSurveyMayaRestart = (interactionId: string) => {
  if (state.mayaRestartAfterOnboardingTransition !== interactionId) return false;
  publish({ type: 'mayaRestarted', interactionId });
  return state.mayaRestartAfterOnboardingTransition !== interactionId;
};
export const receiveNativeSurvey = (input: unknown) => {
  const event = parseSurveyEvent(input);
  if (!event) {
    warnNativeSurveyAdmission('invalid_contract', {
      hasBinding: !!state.binding,
      inputType: Array.isArray(input) ? 'array' : typeof input,
    });
    if (state.binding) publish({ type: 'unavailable' });
    return;
  }
  if (event && event.chatSessionId !== state.binding?.sessionId) {
    const pending = pendingInitialRequests.get(event.responseId);
    if (!pending) {
      warnNativeSurveyAdmission('unknown_initial_response', {
        responseId: event.responseId,
        chatSessionId: event.chatSessionId,
        messageType: event.messageType,
        hasBinding: !!state.binding,
      });
      return;
    }
    if (pending.generation !== generation) {
      warnNativeSurveyAdmission('stale_generation', {
        responseId: event.responseId,
        chatSessionId: event.chatSessionId,
        messageType: event.messageType,
      });
      return;
    }
    if (pending.expiresAt < Date.now()) {
      warnNativeSurveyAdmission('expired_initial_response', {
        responseId: event.responseId,
        chatSessionId: event.chatSessionId,
        messageType: event.messageType,
      });
      removePendingInitialRequest(event.responseId);
      return;
    }
    if (!sameRequestContext(pending, requestContext)) {
      warnNativeSurveyAdmission('request_context_mismatch', {
        responseId: event.responseId,
        chatSessionId: event.chatSessionId,
        messageType: event.messageType,
      });
      return;
    }
    const size = utf8Size(JSON.stringify(event));
    if (pending.events.length >= MAX_PENDING_INITIAL_EVENTS || pending.bytes + size > MAX_PENDING_INITIAL_BYTES) {
      warnNativeSurveyAdmission('initial_buffer_limit', {
        responseId: event.responseId,
        chatSessionId: event.chatSessionId,
        messageType: event.messageType,
        bufferedEvents: pending.events.length,
        bufferedBytes: pending.bytes,
      });
      removePendingInitialRequest(event.responseId);
      return;
    }
    pending.events.push(event);
    pending.bytes += size;
    console.warn('[NativeSurvey] Interactive event buffered pending HTTP session.', {
      responseId: event.responseId,
      chatSessionId: event.chatSessionId,
      messageType: event.messageType,
      bufferedEvents: pending.events.length,
    });
    return;
  }
  const before = state;
  publish({ type: 'event', event });
  if (state === before) {
    warnNativeSurveyAdmission('state_correlation_mismatch', {
      responseId: event.responseId,
      chatSessionId: event.chatSessionId,
      messageType: event.messageType,
      hasBinding: !!state.binding,
    });
  } else {
    console.warn('[NativeSurvey] Interactive event admitted.', {
      responseId: event.responseId,
      chatSessionId: event.chatSessionId,
      messageType: event.messageType,
    });
  }
  if (event.messageType === 'surveyInteractionResult' && event.payload.status === 'retryable' &&
      event.payload.code === 'action_unavailable' && state.phase === 'recovering' && state.pending === before.pending) {
    clearTimeout(pendingTimer);
    const binding = state.binding;
    const frozen = state.pending;
    const startedGeneration = generation;
    if (binding && frozen) {
      void recoverNativeSurvey(binding.sessionId, binding.roomId).then((recovery) => {
        if (generation === startedGeneration && state.pending === frozen) {
          publish({ type: 'actionUnavailableRecovered', recovery });
        }
      }).catch(() => {
        if (generation === startedGeneration && state.pending === frozen) {
          publish({ type: 'actionUnavailableRecoveryFailed' });
        }
      });
    }
  }
};
export const editNativeSurvey = (answer: typeof state.draft) => publish({ type: 'draft', answer });
export const editNativeSurveyHelp = (text: string) => {
  if (state.phase === 'editing' && state.form && canUseGovernedSurveyHelp(state.form)) {
    publish({ type: 'help', open: true, text });
    return;
  }
  publish({ type: 'helpText', text });
};
export const editNativeSurveyClarification = (text: string) => publish({ type: 'clarificationText', text });
export const openNativeSurveyHelp = () => {
  if (state.form && canUseGovernedSurveyHelp(state.form)) publish({ type: 'help', open: true });
};
export const cancelNativeSurveyHelp = () => publish({ type: 'help', open: false });

type Sender = (request: SurveyRequest) => Promise<{ data: { sessionId?: string } }>;
const sendFrozen = async (send: Sender) => {
  const request = state.pending;
  if (!request) return;
  const startedGeneration = generation;
  const interactionId = request.interactiveSurvey.interactionId;
  const reconcileOrMarkUnknown = async () => {
    const awaitingOutcome = () => state.pending === request &&
      ['submitting', 'acceptedAwaitingPresentation'].includes(state.phase);
    if (startedGeneration !== generation || !awaitingOutcome()) return;
    const binding = state.binding;
    if (binding) {
      try {
        const recovery = await recoverNativeSurvey(binding.sessionId, binding.roomId);
        if (startedGeneration !== generation || !awaitingOutcome()) return;
        const before = state;
        publish({ type: 'submissionRecovered', recovery });
        if (state !== before) return;
      } catch { /* handled as an unknown outcome below */ }
    }
    if (startedGeneration === generation && awaitingOutcome()) {
      publish({ type: 'unknown', interactionId });
    }
  };
  clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => { void reconcileOrMarkUnknown(); }, 30000);
  try {
    const response = await send(request);
    if (response.data?.sessionId !== request.sessionId) await reconcileOrMarkUnknown();
  } catch { await reconcileOrMarkUnknown(); }
};
export const submitNativeSurvey = async (operation: SurveyOperation, send: Sender) => {
  if (!state.binding || !state.form || state.pending ||
      !['editing', 'helpComposer', 'answerClarificationComposer'].includes(state.phase) || state.disconnected) return false;
  if (state.recoveryMode) {
    const current = state.recovered[state.recovered.length - 1];
    const currentIsActionable = state.recoveryStatus === 'ready' && state.recoveryMode === 'resume' &&
      hasAnchoredRecoveredCurrent(state.recovered, state.form) && !current?.pendingInteractionId;
    if (!currentIsActionable) return false;
  }
  if (operation.messageType === 'governedSurveyHelp' && state.phase !== 'helpComposer') return false;
  if (state.phase === 'answerClarificationComposer' && operation.messageType !== 'surveyAnswer' &&
      !(operation.messageType === 'surveyAction' && operation.action === 'goBack')) return false;
  try {
    const request = buildSurveyRequest(state.binding, state.form, operation, createAIResponseId());
    publish({ type: 'submit', request, now: Date.now() });
    await sendFrozen(send);
    return true;
  } catch { return false; }
};
export const retryNativeSurvey = async (send: Sender) => {
  const before = state;
  publish({ type: 'retry', now: Date.now() });
  if (state !== before) await sendFrozen(send);
};
